import { useState, useRef } from 'react';
import { doc, updateDoc, arrayUnion, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { getPieceSymbols, PIECE_SYMBOLS_NEUTRAL, isKingRole, PIECE_NAMES } from '../utils/gameUtils';
import { buildMoveUpdateData, buildGameOverUpdate } from '../utils/moveBuilder';
import { findPieceAtSquare } from '../utils/pieceTracking';
import { batchPushChat } from '../utils/chatStore';

// Manages board interaction: move selection, square clicking, drag-and-drop, hover tooltips
export const useBoardInteraction = (gameId, game, chess, currentUser, isAdmin, playerData, playerTeam, setChessPosition, startTimer) => {
  const [moveFrom, setMoveFrom] = useState('');
  const [optionSquares, setOptionSquares] = useState({});
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [previewedMove, setPreviewedMove] = useState(null);

  const boardWrapperRef = useRef(null);

  const hasKingRoleCheck = () => isKingRole(playerData?.role);

  // Check if King is the only active player
  const isKingOnlyMode = () => {
    if (!game || !playerTeam) return false;
    if (!hasKingRoleCheck()) return false;
    const team = playerTeam === 'white' ? game.whiteTeam : game.blackTeam;
    const eliminatedEmails = game.eliminatedPlayers || [];
    const activeVoters = team?.filter(p => {
      if (eliminatedEmails.includes(p.email)) return false;
      return !isKingRole(p.role);
    }) || [];
    return activeVoters.length === 0;
  };

  // Handle piece drop - creates a move suggestion OR executes directly if King is alone
  const onDrop = async ({ sourceSquare, targetSquare }) => {
    if (!game || game.status !== 'in_progress') return false;
    if (game.paused) return false; // host paused — no suggestions/moves
    if (isAdmin || !playerTeam) return false;

    const currentTurnColor = chess.turn() === 'w' ? 'white' : 'black';
    if (playerTeam !== currentTurnColor) return false;

    // Disable during voting
    const activePoll = playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black;
    if (activePoll) return false;

    // Eliminated players can't suggest
    if (game.eliminatedPlayers?.includes(currentUser.email)) return false;

    if (!targetSquare) return false;

    try {
      const move = chess.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q'
      });

      if (move === null) return false;

      // King-only mode: execute move directly
      if (isKingOnlyMode()) {
        const gameRef = doc(db, 'games', gameId);
        const newFen = chess.fen();
        const newTurn = chess.turn() === 'w' ? 'white' : 'black';
        const pieceSymbols = getPieceSymbols(playerTeam);

        const chatMessage = {
          sender: 'SYSTEM',
          message: `King's move: ${pieceSymbols[move.piece] || ''} ${move.san}`,
          timestamp: new Date().toISOString(),
          isMove: true
        };

        const { updateData, chats } = buildMoveUpdateData({
          game, playerTeam, move, sourceSquare, targetSquare,
          newFen, newTurn, chatMessage,
          isKingDirect: true, wasRandom: false, maxVotes: 0,
          hasKingRole: true,
        });

        const batch = writeBatch(db);
        batch.update(gameRef, updateData);
        chats.forEach(c => batchPushChat(batch, gameId, c));
        await batch.commit();
        await startTimer(newTurn, 'suggestion', 90);

        setChessPosition(newFen);
        setMoveFrom('');
        setOptionSquares({});

        // Check for game end
        const gameOverUpdate = buildGameOverUpdate(chess, playerTeam);
        if (gameOverUpdate) {
          await updateDoc(gameRef, gameOverUpdate);
        }

        return true;
      }

      // Normal mode: suggest the move (revert it first)
      chess.undo();

      const pieceSymbol = PIECE_SYMBOLS_NEUTRAL[move.piece] || '';
      const isCapture = move.captured ? ' (captures)' : '';

      const suggestion = {
        id: `${Date.now()}_${currentUser.email}`,
        from: sourceSquare,
        to: targetSquare,
        notation: move.san,
        pieceSymbol,
        displayText: `${pieceSymbol} ${move.san}${isCapture}`,
        player: playerData?.nickname || currentUser.email,
        playerEmail: currentUser.email,
        timestamp: new Date().toISOString(),
        piece: move.piece,
        captured: move.captured || null
      };

      const gameRef = doc(db, 'games', gameId);
      const suggestField = playerTeam === 'white' ? 'suggestedMoves.white' : 'suggestedMoves.black';

      const chatMessage = {
        sender: playerData?.nickname || currentUser.email,
        message: `Suggested move: ${suggestion.displayText}`,
        role: playerData?.role || null,
        timestamp: new Date().toISOString(),
        email: currentUser.email,
        isSuggestion: true,
        team: playerTeam,
      };

      const batch = writeBatch(db);
      batch.update(gameRef, { [suggestField]: arrayUnion(suggestion) });
      batchPushChat(batch, gameId, chatMessage);
      await batch.commit();

      setMoveFrom('');
      setOptionSquares({});

      return false; // Don't visually move the piece
    } catch (error) {
      console.error('Error suggesting move:', error);
      return false;
    }
  };

  // Get move options for a square to show valid moves
  const getMoveOptions = (square) => {
    if (isAdmin || game.status !== 'in_progress' || !playerTeam) return false;
    if (game.paused) return false;

    const currentTurnColor = chess.turn() === 'w' ? 'white' : 'black';
    if (playerTeam !== currentTurnColor) return false;

    const activePoll = playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black;
    if (activePoll) {
      setOptionSquares({});
      return false;
    }

    const moves = chess.moves({ square, verbose: true });
    if (moves.length === 0) {
      setOptionSquares({});
      return false;
    }

    const newSquares = {};
    for (const m of moves) {
      newSquares[m.to] = {
        background:
          chess.get(m.to) && chess.get(m.to).color !== chess.get(square).color
            ? 'radial-gradient(circle, rgba(0,0,0,.1) 85%, transparent 85%)'
            : 'radial-gradient(circle, rgba(0,0,0,.1) 25%, transparent 25%)',
        borderRadius: '50%'
      };
    }
    newSquares[square] = { background: 'rgba(255, 255, 0, 0.4)' };

    setOptionSquares(newSquares);
    return true;
  };

  // Handle square click
  const onSquareClick = async ({ square, piece }) => {
    if (!moveFrom && piece) {
      const hasMoveOptions = getMoveOptions(square);
      if (hasMoveOptions) setMoveFrom(square);
      return;
    }

    const moves = chess.moves({ square: moveFrom, verbose: true });
    const foundMove = moves.find(m => m.from === moveFrom && m.to === square);

    if (!foundMove) {
      const hasMoveOptions = getMoveOptions(square);
      setMoveFrom(hasMoveOptions ? square : '');
      return;
    }

    await onDrop({ sourceSquare: moveFrom, targetSquare: square });
    setMoveFrom('');
    setOptionSquares({});
  };

  // Get piece + assigned player info for the hover tooltip.
  // Returns { pieceLabel, playerLabel, isAI } or null when the square is empty.
  // Works for both human and AI players (both live in whiteTeam/blackTeam with a pieceId).
  const getPieceName = (square) => {
    if (!square) return null;
    const piece = chess.get(square);
    if (!piece) return null;
    const colorName = piece.color === 'w' ? 'White' : 'Black';
    const pieceLabel = `${colorName} ${PIECE_NAMES[piece.type]}`;

    // Look up the player who controls this specific piece via its tracked id.
    let playerLabel = null;
    let isAI = false;
    const trackedPiece = findPieceAtSquare(game?.pieces, game?.moveHistory, square);
    if (trackedPiece) {
      const team = piece.color === 'w' ? game?.whiteTeam : game?.blackTeam;
      const owner = team?.find(p => p.pieceId === trackedPiece.id);
      if (owner) {
        playerLabel = owner.nickname || owner.email;
        isAI = owner.isAI === true || owner.email?.endsWith('@livingchess.local');
      }
    }

    return { pieceLabel, playerLabel, isAI };
  };

  // Calculate which square the mouse is over
  const getSquareFromMousePosition = (e, boardOrientation) => {
    if (!boardWrapperRef.current) return null;

    const rect = boardWrapperRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const squareSize = rect.width / 8;

    let fileIndex = Math.floor(x / squareSize);
    let rankIndex = Math.floor(y / squareSize);

    if (fileIndex < 0 || fileIndex > 7 || rankIndex < 0 || rankIndex > 7) return null;

    if (boardOrientation === 'black') {
      fileIndex = 7 - fileIndex;
      rankIndex = 7 - rankIndex;
    }

    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = ['8', '7', '6', '5', '4', '3', '2', '1'];
    return files[fileIndex] + ranks[rankIndex];
  };

  const handleBoardMouseMove = (e, boardOrientation) => {
    const square = getSquareFromMousePosition(e, boardOrientation);
    if (square !== hoveredSquare) {
      setHoveredSquare(square);
    }
    // Track cursor position (relative to the board wrapper) so the tooltip
    // can follow the pointer instead of sitting under the board.
    if (boardWrapperRef.current) {
      const rect = boardWrapperRef.current.getBoundingClientRect();
      setHoverPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleBoardMouseLeave = () => {
    setHoveredSquare(null);
  };

  // Clear selections (called when game state resets)
  const clearSelections = () => {
    setMoveFrom('');
    setOptionSquares({});
    setPreviewedMove(null);
  };

  return {
    moveFrom,
    optionSquares,
    hoveredSquare,
    hoverPos,
    previewedMove,
    setPreviewedMove,
    boardWrapperRef,
    onDrop,
    onSquareClick,
    getPieceName,
    handleBoardMouseMove,
    handleBoardMouseLeave,
    clearSelections,
  };
};
